import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "~/utils/trpc.client";
import { z } from "zod";

const paramsSchema = z.object({
  temperature: z.number().optional(),
  model: z.string().optional(),
  messages: z.array(z.object({ role: z.string(), content: z.string() })),
});

const resultSchema = z.object({
  choices: z.array(
    z.object({
      message: z.object({
        content: z.string(),
      }),
    }),
  ),
});

export const Route = createFileRoute(
  "/_authed/admin/completions/$completionId",
)({
  component: AdminCompletionsPage,
});

function AdminCompletionsPage() {
  const { completionId } = Route.useParams();

  const completionQuery = trpc.completion.getCompletion.useQuery({
    id: completionId,
  });

  if (completionQuery.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (!completionQuery.data) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-error">Completion not found</div>
      </div>
    );
  }

  const params = paramsSchema.safeParse(completionQuery.data.params);
  const result = resultSchema.safeParse(completionQuery.data.result);

  if (!params.success || !result.success) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-error">Error parsing completion data</div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <div className="flex flex-col gap-4 p-12">
        <h1 className="text-2xl font-bold">Completion Details</h1>

        <div className="card bg-base-100 shadow-xl p-8">
          <div className="text-lg font-semibold mb-4">
            Completion ID: {completionQuery.data.id}
          </div>

          <div className="flex flex-col gap-6">
            <section>
              <h2 className="text-xl font-bold mb-4">Input Parameters</h2>
              <div className="flex gap-4 mb-4">
                <div>Model: {params.data.model}</div>
                <div>Temperature: {params.data.temperature}</div>
                <div>
                  Created:{" "}
                  {new Date(completionQuery.data.createdAt).toLocaleString()}
                </div>
                <div>
                  Last used:{" "}
                  {new Date(completionQuery.data.lastUsed).toLocaleString()}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="font-semibold">Messages:</h3>
                {params.data.messages.map((message, idx) => (
                  <div key={idx} className="card bg-base-200 p-4">
                    <div className="font-medium text-primary mb-2">
                      {message.role}:
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-sm">
                      {message.content}
                    </pre>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-4">Output</h2>
              <div className="card bg-base-200 p-4">
                <pre className="whitespace-pre-wrap font-mono text-sm">
                  {result.data.choices[0]?.message.content}
                </pre>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
